import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Upload, ExternalLink, CheckCircle, Clock, AlertTriangle, AlertCircle,
  FileText, X, Link2, PartyPopper, Plus, Download
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import SupplierSidebar from '../../components/supplier/SupplierSidebar';
import SupplierHeader from '../../components/supplier/SupplierHeader';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { isSupplierArtworkCompleted } from '../../utils/helpers';
import './SupplierArtworkDetail.css';

function StageStatusBanner({ stageNum, stageData, supplierSubmission, isPastStage, isFutureStage, isArtworkCompleted, hasOwnSubmission }) {
  const reopenRequest = supplierSubmission?.reopenRequest;
  if (reopenRequest?.requestedAt && !reopenRequest.resolvedAt) {
    return (
      <div className="supplier-banner supplier-banner--reopen">
        <AlertTriangle size={20} />
        <div>
          <p className="supplier-banner-title">Admin requested changes</p>
          <p className="supplier-banner-text">Please revise this submission and upload the corrected file.</p>
          <p className="supplier-reopen-reason"><strong>Reason:</strong> {reopenRequest.reason}</p>
        </div>
      </div>
    );
  }

  if (isArtworkCompleted) {
    return (
      <div className="supplier-banner supplier-banner--success">
        <PartyPopper size={20} />
        <div>
          <p className="supplier-banner-title">Artwork Fully Approved</p>
          <p className="supplier-banner-text">All four approval stages have been completed successfully.</p>
          {supplierSubmission?.adminApproval?.comment && (
            <p style={{ margin: '10px 0 0 0', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
              <strong>Admin note:</strong> {supplierSubmission.adminApproval.comment}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (isPastStage) {
    return (
      <div className="supplier-banner supplier-banner--done">
        <CheckCircle size={18} />
        <p style={{ margin: 0 }}>This stage has been completed.</p>
      </div>
    );
  }

  if (isFutureStage) {
    return (
      <div className="supplier-banner supplier-banner--neutral">
        <Clock size={18} />
        <p style={{ margin: 0 }}>This stage has not started yet. Earlier stages must be completed first.</p>
      </div>
    );
  }

  if (!hasOwnSubmission) {
    return (
      <div className="supplier-banner supplier-banner--action">
        <AlertTriangle size={20} />
        <div>
          <p className="supplier-banner-title">
            {stageNum === 3 ? 'Upload Your Position Proof' : 'Upload Colour Proof Scan'}
          </p>
          <p className="supplier-banner-text">
            {stageNum === 3
              ? 'Prepare your ready-to-print version adapted to your key line drawings and upload it below.'
              : 'You have couriered hard copies. Please also upload a scan of the colour proof for the approval team to record.'}
          </p>
        </div>
      </div>
    );
  }

  if (supplierSubmission?.status !== 'completed' && (supplierSubmission?.approvals || []).some(a => a.status === 'changes_requested')) {
    return (
      <div className="supplier-banner supplier-banner--warning">
        <AlertTriangle size={20} />
        <div>
          <p className="supplier-banner-title">Changes Requested</p>
          <p className="supplier-banner-text">
            The approval team has requested changes. Please revise and re-upload.
          </p>
        </div>
      </div>
    );
  }

  if (stageNum === 4 && supplierSubmission?.status === 'completed' && !(supplierSubmission?.adminApproval && supplierSubmission.adminApproval.uploadedAt)) {
    return (
      <div className="supplier-banner supplier-banner--info">
        <Clock size={20} />
        <div>
          <p className="supplier-banner-title">Awaiting Final Sign-off</p>
          <p className="supplier-banner-text">
            All reviewers have approved your colour proof. The CHI admin team will upload the final scanned approval copy shortly.
          </p>
        </div>
      </div>
    );
  }

  if (stageNum === 4 && supplierSubmission?.adminApproval && supplierSubmission.adminApproval.uploadedAt) {
    return (
      <div className="supplier-banner supplier-banner--success">
        <CheckCircle size={18} />
        <p style={{ margin: 0 }}>This submission has been fully approved and finalized.</p>
      </div>
    );
  }

  if (supplierSubmission?.status !== 'completed') {
    return (
      <div className="supplier-banner supplier-banner--info">
        <Clock size={20} />
        <div>
          <p className="supplier-banner-title">Under Internal Review</p>
          <p className="supplier-banner-text">
            Your submission is being reviewed by the CHI team. We'll notify you when it's complete.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

function UploadPanel({ artworkId, stageNum, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'application/pdf': [] },
    maxSize: 20 * 1024 * 1024,
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return toast.error('Please select at least one file');
    setUploading(true);
    try {
      let uploadedImage = null;
      let uploadedDocument = null;

      // Upload each file
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const uploadRes = await api.post('/upload', fd);

        if (uploadRes.data.isImage) {
          uploadedImage = {
            artworkUrl: uploadRes.data.url,
            artworkPublicId: uploadRes.data.publicId
          };
        } else {
          uploadedDocument = {
            artworkUrl: uploadRes.data.url || null,
            artworkPublicId: uploadRes.data.publicId || null,
            artworkFileName: uploadRes.data.fileName,
            artworkMimeType: uploadRes.data.mimeType,
            artworkData: uploadRes.data.data || null
          };
        }
      }

      // Prepare the update payload
      const uploadData = { stage: stageNum };
      if (uploadedImage) Object.assign(uploadData, uploadedImage);
      if (uploadedDocument) Object.assign(uploadData, uploadedDocument);

      await api.patch(`/artworks/${artworkId}/supplier-upload`, uploadData);
      toast.success('Files uploaded successfully! The approval team has been notified.');
      setFiles([]);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="supplier-upload-panel">
      <h4 className="supplier-upload-panel-title">
        <Upload size={16} />
        {stageNum === 3 ? 'Upload Position Proof' : 'Upload Colour Proof Scan'}
      </h4>
      <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginBottom: 16 }}>
        {stageNum === 3
          ? 'Upload your PDF or image showing the artwork adapted to your key line drawing. You can select multiple files.'
          : 'Upload scans or high-quality photos of the colour proof you have couriered to CHI. You can select multiple files.'}
      </p>

      {files.length === 0 ? (
        <div
          {...getRootProps()}
          className={`supplier-dropzone ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="supplier-dropzone-icon">
            <Upload size={26} />
          </div>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>
            {isDragActive ? 'Drop your files here' : 'Drag & drop your files here'}
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
            or click to browse · PNG, JPG, PDF · Max 20MB each
          </p>
        </div>
      ) : (
        <>
          <div className="file-grid">
            {files.map((file, idx) => (
              <div key={idx} className="file-card">
                <div className="file-card-head">
                  <div>
                    <p className="file-card-name" style={{ maxWidth: 130 }}>{file.name}</p>
                    <p className="file-card-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button
                    type="button"
                    className="file-card-remove"
                    onClick={() => removeFile(idx)}
                    aria-label="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="file-card-thumb">
                  {file.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(file)} alt="preview" />
                  ) : (
                    <div className="file-card-doc">
                      <FileText size={28} color="#ef4444" strokeWidth={1.5} />
                      <p>PDF</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="upload-actions">
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading}
              style={{ flex: 1 }}
            >
              {uploading ? <><span className="spinner" /> Uploading…</> : <><Upload size={15} /> Submit All</>}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setFiles([])}
              disabled={uploading}
              style={{ flex: 1 }}
            >
              Clear All
            </button>
          </div>

          <div {...getRootProps()} className="add-more-row">
            <input {...getInputProps()} />
            <Plus size={14} />
            Add more files
          </div>
        </>
      )}
    </div>
  );
}

export default function SupplierArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout, supplier } = useAuth();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [zoom, setZoom] = useState(100);
  const imageRef = React.useRef(null);

  const getSupplierStageSubmission = useCallback((stageData) => {
    if (!supplier || !stageData?.submissions) return null;
    const supplierId = supplier._id || supplier.id;
    return stageData.submissions.find((submission) => String(submission.supplierId) === String(supplierId)) || null;
  }, [supplier]);

  // wheel zoom handler (Ctrl + scroll)
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -20 : 20;
      setZoom(prev => Math.max(50, Math.min(500, prev + delta)));
    }
  }, []);

  useEffect(() => {
    const el = imageRef.current;
    if (el) {
      el.addEventListener('wheel', handleWheel, { passive: false });
      return () => el.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  const downloadStageFile = async (stageData) => {
    try {
      if (!stageData) return toast.error('No file to download');

      if (stageData.artworkData) {
        const binaryString = atob(stageData.artworkData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const blob = new Blob([bytes.buffer], { type: stageData.artworkMimeType || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = stageData.artworkFileName || 'artwork.pdf';
        document.body.appendChild(a);
        a.click(); a.remove(); URL.revokeObjectURL(url);
        return;
      }

      if (stageData.artworkUrl) {
        const resp = await fetch(stageData.artworkUrl);
        if (!resp.ok) throw new Error('Failed to fetch file');
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const name = stageData.artworkFileName || stageData.artworkUrl.split('/').pop().split('?')[0] || 'artwork';
        const ext = (blob.type && blob.type.split('/')[1]) || 'png';
        a.download = name.includes('.') ? name : `${name}.${ext}`;
        document.body.appendChild(a);
        a.click(); a.remove(); URL.revokeObjectURL(url);
        return;
      }

      toast.error('No downloadable file found');
    } catch (err) {
      console.error('Download error', err);
      toast.error('Failed to download file');
    }
  };

  const fetchArtwork = useCallback(async () => {
    try {
      const res = await api.get(`/artworks/${id}`);
      setArtwork(res.data);
    } catch { toast.error('Failed to load artwork'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchArtwork(); }, [fetchArtwork]);

  const assignedSupplierNames = React.useMemo(() => {
    const map = {};
    artwork?.supplierAssignmentInfo?.assignedSuppliers?.forEach((supplier) => {
      if (supplier?._id) {
        map[String(supplier._id)] = supplier.name || supplier.email || 'Supplier';
      }
    });
    return map;
  }, [artwork]);

  const currentSupplierId = supplier?._id || supplier?.id;

  useEffect(() => {
    if (showLogoutConfirm) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showLogoutConfirm]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    navigate('/supplier/login');
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div className="spinner spinner-dark" style={{ width: 36, height: 36 }} />
    </div>
  );

  if (!artwork) return null;

  const supplierStages = [3, 4]; // Supplier only sees stages 3 and 4

  return (
    <>
      <div className="page-layout supplier-detail-page">
        <SupplierSidebar onLogout={handleLogout} />
        <div className="main-content">
          <SupplierHeader
            title={artwork.title}
            unread={0}
            notifications={[]}
            onMarkAllRead={() => {}}
          />
          <div className="page-body">

            {/* SharePoint Link Info */}
            {artwork.sharePointLink && (
              <div className="sharepoint-card">
                <div>
                  <p className="sharepoint-card-title">
                    <Link2 size={15} />
                    SharePoint Link Available
                  </p>
                  <p className="sharepoint-card-text">
                    Access CHI Limited's SharePoint folder for this artwork project. You'll find additional brand guidelines, technical specifications, brand assets, and other supporting files to help you prepare your position proof.
                  </p>
                </div>
                <a href={artwork.sharePointLink} target="_blank" rel="noopener noreferrer" className="btn btn-info btn-sm" style={{ whiteSpace: 'nowrap' }}>
                  <ExternalLink size={14} /> Open SharePoint
                </a>
              </div>
            )}

            {/* Approved artwork from Stage 2 (reference) */}
            {(artwork.stage2?.artworkUrl || artwork.stage2?.artworkData) && artwork.stage2?.status === 'completed' && (
              <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                <p className="reference-card-title">
                  <CheckCircle size={16} />
                  Approved Artwork (Reference)
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginBottom: 14 }}>
                  This is the final approved artwork from CHI. Use this as reference for your position proof.
                </p>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Image Preview */}
                  {artwork.stage2.artworkUrl && artwork.stage2.artworkUrl.match(/\.(jpg|jpeg|png|webp)$/i) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className="reference-preview">
                        <img src={artwork.stage2.artworkUrl} alt="approved" />
                      </div>
                      <a href={artwork.stage2.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                        <ExternalLink size={14} /> Open Full Size
                      </a>
                    </div>
                  )}

                  {/* PDF Preview */}
                  {artwork.stage2.artworkData && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className="reference-preview">
                        <FileText size={36} color="#ef4444" strokeWidth={1.5} />
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                          <p style={{ fontWeight: 600, margin: 0 }}>PDF Document</p>
                          <p style={{ margin: '2px 0 0 0', color: 'var(--gray-400)' }}>Click to view</p>
                        </div>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          try {
                            const binaryString = atob(artwork.stage2.artworkData);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                              bytes[i] = binaryString.charCodeAt(i);
                            }
                            const blob = new Blob([bytes], { type: artwork.stage2.artworkMimeType || 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (error) {
                            console.error('Error opening PDF:', error);
                            toast.error('Could not open file: Invalid file data');
                          }
                        }}
                      >
                        <ExternalLink size={14} /> Open Full Size
                      </button>
                    </div>
                  )}

                  {/* PDF from URL (legacy) */}
                  {artwork.stage2.artworkUrl && artwork.stage2.artworkUrl.match(/\.pdf$/i) && !artwork.stage2.artworkData && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className="reference-preview">
                        <FileText size={36} color="#ef4444" strokeWidth={1.5} />
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                          <p style={{ fontWeight: 600, margin: 0 }}>PDF Document</p>
                          <p style={{ margin: '2px 0 0 0', color: 'var(--gray-400)' }}>Click to view</p>
                        </div>
                      </div>
                      <a href={artwork.stage2.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                        <ExternalLink size={14} /> Open Full Size
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stage panels for supplier */}
            {supplierStages.map(stageNum => {
              const stageData = artwork[`stage${stageNum}`];
              const supplierSubmission = getSupplierStageSubmission(stageData);

              // Each supplier moves through Stage 3 -> Stage 4 independently, so
              // "is this stage active for me" is derived from my own submission
              // state rather than the artwork's single global currentStage field.
              const stage3Submission = getSupplierStageSubmission(artwork.stage3);
              const myStage3Done = stage3Submission?.status === 'completed';
              const myArtworkCompleted = isSupplierArtworkCompleted(artwork, supplier?._id || supplier?.id);
              const isCurrentStage = stageNum === 3
                ? (artwork.currentStage >= 3 && !myStage3Done)
                : (myStage3Done && !(supplierSubmission?.adminApproval && supplierSubmission.adminApproval.uploadedAt));
              const isPastStage = stageNum === 3 ? myStage3Done : Boolean(supplierSubmission?.adminApproval && supplierSubmission.adminApproval.uploadedAt);
              const isFutureStage = stageNum === 4 && !myStage3Done;

              const hasActiveReopenRequest = Boolean(supplierSubmission?.reopenRequest?.requestedAt && !supplierSubmission.reopenRequest.resolvedAt);
              const supplierHasChangesRequested = hasActiveReopenRequest || (supplierSubmission?.approvals || []).some(a => a.status === 'changes_requested');
              const hasOwnSubmission = Boolean(supplierSubmission?.artworkUrl || supplierSubmission?.artworkData || supplierSubmission?.artworkFileName);
              // Show upload panel only when the supplier has not uploaded any file yet,
              // or when changes have been explicitly requested.
              const needsUpload = isCurrentStage && (!hasOwnSubmission || supplierHasChangesRequested);

              return (
                <div key={stageNum} className="card stage-card">
                  <div className="stage-card-header">
                    <div className="stage-card-heading">
                      <div className="stage-number" style={{
                        background: `var(--stage${stageNum}-light)`,
                        color: `var(--stage${stageNum})`
                      }}>
                        {stageNum}
                      </div>
                      <div>
                        <p className="stage-card-title">
                          {stageNum === 3 ? 'Stage 3: Position Proof' : 'Stage 4: Colour Proof / Chromalin'}
                        </p>
                        <p className="stage-card-subtitle">
                          {stageNum === 3
                            ? 'Submit your key-line adapted ready-to-print version'
                            : 'Submit colour proof scan after couriering hard copies to CHI'}
                        </p>
                      </div>
                    </div>
                    <span className={`badge status-badge ${
                      isPastStage ? 'badge-success' :
                      supplierHasChangesRequested ? 'badge-danger' :
                      supplierSubmission ? 'badge-info' :
                      'badge-gray'
                    }`}>
                      {isPastStage ? <><CheckCircle size={12} /> Completed</> :
                       supplierHasChangesRequested ? <><AlertTriangle size={12} /> Changes Needed</> :
                       supplierSubmission ? <><Clock size={12} /> Under Review</> :
                       'Not Started'}
                    </span>
                  </div>

                  <div className="stage-card-body">
                    <StageStatusBanner
                      stageNum={stageNum}
                      stageData={stageData}
                      supplierSubmission={supplierSubmission}
                      isPastStage={isPastStage}
                      isFutureStage={isFutureStage}
                      isArtworkCompleted={stageNum === 4 && myArtworkCompleted}
                      hasOwnSubmission={hasOwnSubmission}
                    />

                            {/* Show current submission */}
                    {stageData?.submissions?.length > 0 && (
                      <div style={{ marginTop: 16, marginBottom: needsUpload ? 16 : 0 }}>
                        <p className="submission-label">
                          <FileText size={14} />
                          Supplier Uploads
                        </p>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {stageData.submissions.map((submission) => {
                            const supplierName = assignedSupplierNames[String(submission.supplierId)] || String(submission.supplierId || 'Supplier');
                            const isCurrentSupplier = String(submission.supplierId) === String(currentSupplierId);

                            return (
                              <div key={String(submission.supplierId)} className="submission-card" style={{ flex: '1 1 280px', minWidth: 280 }}>
                                <div className="submission-card-header">
                                  <div>
                                    <p className="submission-card-title">{supplierName}</p>
                                    <p className="submission-card-subtitle">Supplier Upload</p>
                                  </div>
                                  {isCurrentSupplier && <span className="badge badge-info" style={{ fontSize: '0.6875rem' }}>You</span>}
                                </div>
                                <div className="submission-preview" style={{ opacity: submission.artworkData ? 1 : 1 }}>
                                  {submission.artworkUrl && submission.artworkUrl.match(/\.(jpg|jpeg|png|webp)/i) && !submission.artworkData
                                    ? <img src={submission.artworkUrl} alt="proof" style={{ transform:`scale(${zoom/100})`, maxWidth:'100%', maxHeight:'100%'}} />
                                    : submission.artworkData || (submission.artworkUrl && submission.artworkUrl.includes('.pdf'))
                                    ? <div className="submission-preview-doc">
                                        <FileText size={32} color="#ef4444" strokeWidth={1.5} />
                                        <p style={{ fontSize: '0.8125rem', fontWeight: 500, margin: 0 }}>PDF Document</p>
                                      </div>
                                    : submission.artworkUrl
                                    ? <img src={submission.artworkUrl} alt="proof" style={{ transform:`scale(${zoom/100})`, maxWidth:'100%', maxHeight:'100%'}} />
                                    : <div style={{ padding: 16, color: 'var(--gray-500)' }}>No file uploaded</div>
                                  }
                                </div>
                                <div style={{ display:'flex', gap:8, alignItems:'center', marginTop: 12 }}>
                                  {submission.artworkUrl && (
                                    <a href={submission.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                                      <ExternalLink size={14} /> View Full Size
                                    </a>
                                  )}
                                  {submission.artworkData && (
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={() => {
                                        try {
                                          const binaryString = atob(submission.artworkData);
                                          const bytes = new Uint8Array(binaryString.length);
                                          for (let i = 0; i < binaryString.length; i++) {
                                            bytes[i] = binaryString.charCodeAt(i);
                                          }
                                          const blob = new Blob([bytes], { type: submission.artworkMimeType || 'application/pdf' });
                                          const url = URL.createObjectURL(blob);
                                          window.open(url, '_blank');
                                        } catch (error) {
                                          console.error('Error opening PDF:', error);
                                          toast.error('Could not open file: Invalid file data');
                                        }
                                      }}
                                    >
                                      <ExternalLink size={14} /> View Full Size
                                    </button>
                                  )}
                                  <button className="btn btn-sm btn-secondary" onClick={() => downloadStageFile(submission)}>
                                    <Download size={14} /> Download
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Upload panel */}
                    {needsUpload && (
                      <div style={{ marginTop: 16 }}>
                        <UploadPanel artworkId={id} stageNum={stageNum} onSuccess={fetchArtwork} />
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-card-head">
              <div className="modal-icon">
                <AlertCircle size={18} />
              </div>
              <h3 className="modal-card-title">Sign Out?</h3>
            </div>
            <p className="modal-card-text">
              Are you sure you want to sign out? You'll need to log in again to access your account.
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn modal-btn--cancel"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn modal-btn--danger"
                onClick={confirmLogout}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}